// BEARER BRIDGE - turn an SWA-authenticated session into an MSAL bearer token
// ============================================================================
// WHY THIS EXISTS
// ---------------
// PR #630 removed `x-ms-client-principal` as an authentication source in
// api/shared/auth.ts. After it, requireAuth accepts only a service key, a
// `hub_` API key, or a JWKS-verifiable MSAL JWT. Every staff-gated endpoint -
// including all four handoff routes, which gate on requireStaff - now returns
// 401 to a browser that sends no Authorization header.
//
// The SPA was never updated to match. AuthGuard authenticates the app shell
// through SWA (`/.auth/me`, `/.auth/login/aad`), which establishes an SWA
// session but NOT an MSAL account. msalConfig caches accounts in
// sessionStorage, so a new tab or a browser restart routinely leaves a user
// who is fully SWA-authenticated with zero MSAL accounts. getAccessToken()
// returned null in that state and the callers deliberately proceeded with no
// Authorization header - which was survivable only while the header fallback
// existed on the server.
//
// This module is the missing half. Given a live SWA session it establishes an
// MSAL account via ssoSilent with OpenID scopes (the user already holds an
// Entra session cookie, so this is silent in the common case) and returns a
// real bearer token.
//
// IT DOES NOT RESTORE ANY FORGEABLE TRUST PATH. Nothing here reads, forwards,
// or re-trusts `x-ms-client-principal`. The SWA identity is used for exactly
// one thing: as a `loginHint` string telling MSAL WHICH account to sign in.
// The resulting token is minted by Entra and verified by the API against JWKS
// as before, so a forged hint yields either no token or a token for the
// forger's own identity - it cannot mint someone else's claims. That is the
// bounded, verifiable property that makes using the SWA identity here safe.
// ============================================================================

// ----- Injected surface ------------------------------------------------------
// Everything MSAL- and browser-specific is injected so this module stays
// importable (and unit-testable) under plain Node, where `import.meta.env`,
// `window` and @azure/msal-browser are all unavailable.

export interface MsalAccountLike {
  username?: string;
  homeAccountId?: string;
}

export interface SilentTokenResult {
  accessToken?: string;
  idToken?: string;
}

/**
 * What the SWA identity probe established.
 *
 * The distinction is load-bearing. A single `string | null` collapsed four
 * different situations into one, and "probe failed" then took the same branch
 * as "not behind SWA" - which meant a 3s timeout or a 502 on /.auth/me was
 * enough to fall back to the first cached sessionStorage account without ever
 * comparing it to the current user. That reintroduces exactly the
 * identity-confusion case the hint matching exists to prevent.
 *
 *  - 'signed-in'  we know who the user is; the account MUST match
 *  - 'signed-out' behind SWA, nobody signed in
 *  - 'no-swa'     the SWA auth platform is not in front of us (local dev)
 *  - 'unknown'    the probe failed, timed out, or errored - we know NOTHING,
 *                 and must not guess
 */
export type SwaIdentity =
  | { state: 'signed-in'; upn: string }
  | { state: 'signed-out' }
  | { state: 'no-swa' }
  | { state: 'unknown' };

export interface BearerBridgeDeps {
  /**
   * Awaited before ANY other MSAL call.
   *
   * @azure/msal-browser v3 requires initialize() to have resolved before
   * ssoSilent, acquireTokenSilent, loginRedirect or getAllAccounts are used;
   * otherwise they throw `uninitialized_public_client_application`. Nothing in
   * this app called initialize(), so every acquisition threw into a swallowing
   * catch and resolved to "no token" - which is why the bridge alone would not
   * have fixed the incident. Making readiness an explicit dependency of
   * acquireBearer is what stops that ordering being re-broken silently.
   */
  ready(): Promise<void>;
  /**
   * Whether an MSAL account with no SWA identity to check it against may be
   * used. TRUE ONLY IN A DEV BUILD. Production wiring sets this from
   * import.meta.env.DEV, so in a deployed bundle the unhinted path is not
   * merely unused - it is unreachable, whatever the probe returns.
   */
  allowUnhintedAccount: boolean;
  /** Accounts MSAL currently knows about in this browser context. */
  getAccounts(): MsalAccountLike[];
  /**
   * MSAL ssoSilent. Establishes an account from an existing IdP session
   * without user interaction. Rejects when interaction is required.
   */
  ssoSilent(req: { scopes: string[]; loginHint: string }): Promise<MsalAccountLike | null>;
  /** MSAL acquireTokenSilent. */
  acquireTokenSilent(req: { scopes: string[]; account: MsalAccountLike }): Promise<SilentTokenResult>;
  /**
   * Who SWA says is signed in, and how confidently we know it.
   * The UPN is used ONLY as an MSAL loginHint - never as an identity assertion.
   */
  getSwaIdentity(): Promise<SwaIdentity>;
}

/**
 * Why a bearer token could not be produced.
 *
 *  - 'ok'                   a token was returned
 *  - 'not-configured'       MSAL is not configured in this build
 *  - 'no-session'           no MSAL account and no SWA session to bridge from;
 *                           the user is genuinely signed out
 *  - 'interaction-required' there IS a session but silent acquisition failed;
 *                           the caller should escalate to interactive sign-in
 *  - 'identity-unknown'     the SWA identity probe failed, so we cannot say who
 *                           the user is. Deliberately does NOT escalate: we do
 *                           not know who to sign in, and the probe re-runs on
 *                           the next call.
 */
export type BearerReason =
  | 'ok'
  | 'not-configured'
  | 'no-session'
  | 'interaction-required'
  | 'identity-unknown';

export interface BearerResult {
  token: string | null;
  reason: BearerReason;
}

/** Scope for a token whose audience is this app's own API registration. */
export function apiScopeFor(clientId: string): string {
  return 'api://' + clientId + '/.default';
}

/** Fallback scopes - yields an id token for single-registration SPA + API setups. */
export const ID_TOKEN_SCOPES = ['openid', 'profile', 'email'];

// ----- Core ------------------------------------------------------------------

/**
 * Produce a bearer token for a staff-gated /api call.
 *
 * Order, all load-bearing:
 *   1. Use an existing MSAL account when there is one.
 *   2. Otherwise bridge: ask SWA who is signed in, and use that as a loginHint
 *      for ssoSilent with OpenID scopes. This is the case that was broken -
 *      SWA-authenticated, MSAL-empty.
 *   3. With an account in hand, prefer an access token scoped to the API
 *      registration; fall back to the id token.
 *
 * Never throws. Callers get a reason so they can decide whether escalating to
 * an interactive sign-in is warranted.
 */
export async function acquireBearer(
  deps: BearerBridgeDeps,
  apiScope: string | null,
): Promise<BearerResult> {
  if (!apiScope) return { token: null, reason: 'not-configured' };

  // MSAL v3: nothing below may run until initialize() has resolved.
  try {
    await deps.ready();
  } catch {
    // Initialisation failed - MSAL is unusable in this page. Report it as a
    // configuration fault rather than interaction-required, so we do NOT send
    // the user into an interactive redirect that cannot succeed either.
    return { token: null, reason: 'not-configured' };
  }

  // Establish WHO before looking at MSAL's cache.
  //
  // MSAL caches accounts in sessionStorage; SWA is the authority on who is
  // using the app right now. If we cannot establish the current identity we
  // must not fall back to whatever is cached - a stale account from a previous
  // user would mint a genuine token for the wrong person, and the API would
  // verify it happily because it is real, just not theirs.
  let identity: SwaIdentity;
  try {
    identity = await deps.getSwaIdentity();
  } catch {
    identity = { state: 'unknown' };
  }

  if (identity.state === 'signed-out') return { token: null, reason: 'no-session' };

  if (identity.state === 'unknown') {
    // Fail closed. The probe is bounded and its cache is dropped on failure,
    // so the next call re-probes: a transient blip costs one request, not a
    // lockout, and never a token minted for an unverified identity.
    return { token: null, reason: 'identity-unknown' };
  }

  if (identity.state === 'no-swa' && !deps.allowUnhintedAccount) {
    // Deployed builds are always behind SWA. Reaching here in production means
    // something is wrong with the platform, not that we are in local dev, so
    // the unhinted fallback stays shut.
    return { token: null, reason: 'identity-unknown' };
  }

  const hint = identity.state === 'signed-in' ? identity.upn : null;
  let account = selectAccount(deps, hint);

  if (!account) {
    // Either MSAL holds nothing, or everything it holds belongs to a different
    // user than the one SWA reports. Both need a fresh sign-in for the CURRENT
    // identity; neither may reuse what is cached.
    if (!hint) return { token: null, reason: 'no-session' };

    try {
      const bridged = await deps.ssoSilent({ scopes: ID_TOKEN_SCOPES, loginHint: hint });
      account = bridged ?? selectAccount(deps, hint);
    } catch {
      // ssoSilent rejects with InteractionRequiredAuthError when the Entra
      // session cannot be reused silently (no session cookie, MFA due,
      // third-party cookies blocked). That is recoverable interactively.
      return { token: null, reason: 'interaction-required' };
    }
    if (!account) return { token: null, reason: 'interaction-required' };
  }

  // Prefer an access token whose audience is the API registration.
  try {
    const result = await deps.acquireTokenSilent({ scopes: [apiScope], account });
    if (result && result.accessToken) return { token: result.accessToken, reason: 'ok' };
  } catch {
    // fall through to the id token attempt
  }

  try {
    const result = await deps.acquireTokenSilent({ scopes: ID_TOKEN_SCOPES, account });
    if (result && result.idToken) return { token: result.idToken, reason: 'ok' };
  } catch {
    return { token: null, reason: 'interaction-required' };
  }

  return { token: null, reason: 'interaction-required' };
}

/**
 * Pick the MSAL account for the identity SWA currently reports.
 *
 * With a hint, ONLY an account matching it is acceptable - a non-matching
 * cached account is treated as absent rather than used, which is what stops a
 * previous user's token being minted for the current one.
 *
 * With no hint (local dev, or not running behind SWA) there is nothing to
 * match against, so the first account is used as before.
 */
export function selectAccount(
  deps: BearerBridgeDeps,
  hint: string | null,
): MsalAccountLike | null {
  let accounts: MsalAccountLike[];
  try {
    accounts = deps.getAccounts() || [];
  } catch {
    return null;
  }
  if (!accounts.length) return null;
  if (!hint) return accounts[0];

  const wanted = hint.trim().toLowerCase();
  return accounts.find((a) => (a.username || '').trim().toLowerCase() === wanted) ?? null;
}

// ----- MSAL v3 lifecycle -----------------------------------------------------
// Two calls the app never made, both mandatory in @azure/msal-browser v3:
//
//   initialize()            - must resolve before any other MSAL API, or every
//                             call throws uninitialized_public_client_application
//   handleRedirectPromise() - must run once on load to redeem the response of
//                             an interactive redirect; without it a user who
//                             completes sign-in returns to the app with the
//                             result unclaimed and no account, so the redirect
//                             accomplishes nothing and can loop
//
// Both are memoised: initialize is idempotent but handleRedirectPromise is NOT
// - the redirect response is consumed on first read, so calling it per-request
// would redeem on one call and return null on the rest, and which call wins
// would be a race.

export interface MsalLifecycleDeps {
  initialize(): Promise<void>;
  /** Resolves to the redeemed result, or null when this load is not a redirect return. */
  handleRedirectPromise(): Promise<{ account?: MsalAccountLike | null } | null>;
  /** Adopt the redeemed account as active, when the client supports it. */
  setActiveAccount?(account: MsalAccountLike): void;
}

export interface MsalReadyResult {
  /** The account redeemed from an interactive redirect return, if any. */
  redeemedAccount: MsalAccountLike | null;
}

let _ready: Promise<MsalReadyResult> | null = null;

/** Test seam - drops the memoised lifecycle so ordering can be re-asserted. */
export function __resetMsalReadyForTests(): void {
  _ready = null;
}

/**
 * Initialise MSAL and redeem any pending redirect, exactly once per page.
 *
 * A rejection drops the memo so a later call can retry rather than leaving the
 * app permanently unable to authenticate after one transient failure.
 */
export function ensureMsalReady(deps: MsalLifecycleDeps): Promise<MsalReadyResult> {
  if (!_ready) {
    const run = (async (): Promise<MsalReadyResult> => {
      await deps.initialize();

      let redeemedAccount: MsalAccountLike | null = null;
      try {
        const result = await deps.handleRedirectPromise();
        redeemedAccount = result?.account ?? null;
        if (redeemedAccount && deps.setActiveAccount) {
          deps.setActiveAccount(redeemedAccount);
        }
      } catch {
        // Not a redirect return, or the response could not be redeemed. Not
        // fatal: the silent path below is still worth attempting.
        redeemedAccount = null;
      }

      return { redeemedAccount };
    })();

    _ready = run;
    run.catch(() => {
      _ready = null;
    });
  }
  return _ready;
}

// ----- Interactive escalation guard ------------------------------------------
// An interactive sign-in is a full-page redirect, so fresh attempts must be
// coalesced. Without a guard, a page that issues several parallel /api calls
// would each try to redirect, and a redirect that lands back in the same
// unauthenticated state would loop. The guard is time-bound so an abandoned or
// failed redirect cannot lock the browser out until sessionStorage is cleared.
//
// The previous code had a guard of this shape but pointed it at
// `/.auth/login/aad` - the SWA session login, which after #630 cannot produce
// a credential the API accepts. Re-authenticating there and returning still
// 401s, which is the loop analysts hit. Escalation must go to MSAL.

export const INTERACTIVE_AUTH_FLAG = 'msal-interactive-auth-in-progress';
export const INTERACTIVE_AUTH_FLAG_MAX_AGE_MS = 5 * 60 * 1000;

export interface SessionFlagStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * Does this reason call for an interactive sign-in?
 *
 * THE single source of truth, used both by shouldStartInteractiveAuth and by
 * the typed client's 401 handler. Only 'interaction-required' qualifies:
 *
 *  - 'no-session'       the user is signed out of SWA too; AuthGuard owns that
 *                       at the shell level
 *  - 'not-configured'   a build or MSAL-init fault no redirect can fix
 *  - 'identity-unknown' we cannot say WHO to sign in, so a redirect would be a
 *                       guess; the probe re-runs on the next call instead
 */
export function reasonWarrantsInteraction(reason: BearerReason): boolean {
  return reason === 'interaction-required';
}

/**
 * True when an interactive sign-in should be started now. Records the attempt
 * so subsequent callers stand down while the attempt is fresh.
 */
export function shouldStartInteractiveAuth(
  reason: BearerReason,
  store: SessionFlagStore | null,
  now: number = Date.now(),
): boolean {
  if (!reasonWarrantsInteraction(reason)) return false;
  if (!store) return false;
  try {
    const existing = store.getItem(INTERACTIVE_AUTH_FLAG);
    if (existing) {
      const startedAt = Number(existing);
      const age = now - startedAt;
      if (Number.isFinite(startedAt) && startedAt > 0 && age >= 0 && age < INTERACTIVE_AUTH_FLAG_MAX_AGE_MS) {
        return false;
      }
      store.removeItem(INTERACTIVE_AUTH_FLAG);
    }
    store.setItem(INTERACTIVE_AUTH_FLAG, String(now));
    return true;
  } catch {
    return false;
  }
}

/** Clear the guard once a token has been obtained. */
export function clearInteractiveAuthFlag(store: SessionFlagStore | null): void {
  if (!store) return;
  try {
    store.removeItem(INTERACTIVE_AUTH_FLAG);
  } catch {
    // storage unavailable - nothing to clear
  }
}
